import React from 'react';
import {FormattedMessage} from 'react-intl';
import {useFieldArray, useWatch} from 'react-hook-form';
import {useLazyLoadQuery} from 'react-relay/hooks';
import {useAuthorizationState} from '../../../contexts/Authorization';
import {graphqlWhoamiNextConditionalQuery} from '../../../components/graphql';
import Button from '../../../components/button/button';
import XCloseIcon from '../../../assets/icons0/x-close.svg';
import UploadCloud02Icon from '../../../assets/icons0/upload-cloud-02.svg';
import ArrowBlockUpIcon from '../../../assets/icons0/arrow-block-up.svg';
import Trash03Icon from '../../../assets/icons0/trash-03.svg';
import IconFeature from '../../../components/icon-feature/icon-feature';
import classNames from 'classnames';
import {upload} from '../../../fh';
import LogoLoading from '../../../assets/logo-loading.svg';
import SuspenseImage from '../../../components/SuspenseImageDynamicSize';
import Skeleton from '../../../components/Skeleton';
import {ErrorBoundary} from 'react-error-boundary';
import ErrorFallback from '../../../components/ErrorFallback';
import './media-form.scss';

const MediaForm = React.memo(({control, setValue, formState, onRemove}) => {
    const media = useWatch({control, name: 'media'}) || [];
    const authorized = useAuthorizationState();
    const {whoamiNext} = useLazyLoadQuery(graphqlWhoamiNextConditionalQuery, {
        includeWhoamiNext: authorized
    });
    const currentUserId = whoamiNext?.id;

    const {fields, append, remove, update} = useFieldArray({
        control,
        name: 'media',
        rules: {maxLength: 4}
    });

    const [uploadingIndices, setUploadingIndices] = React.useState(new Set());
    const [draggingIndices, setDraggingIndices] = React.useState(new Set());
    const [errors, setErrors] = React.useState(new Map());

    const maxImages = 4;

    const startDragging = React.useCallback((index) => (e) => {
        e.preventDefault();
        setDraggingIndices(prev => new Set(prev).add(index));
    }, []);

    const endDragging = React.useCallback((index) => (e) => {
        e.preventDefault();
        setDraggingIndices(prev => {
            const next = new Set(prev);
            next.delete(index);
            return next;
        });
    }, []);

    const setError = React.useCallback((index, message) => {
        setErrors(prev => {
            const next = new Map(prev);
            if (message) {
                next.set(index, message);
            } else {
                next.delete(index);
            }
            return next;
        });
    }, []);

    const dropping = React.useCallback((index) => (e) => {
        e.preventDefault();
        const file = e?.dataTransfer?.files[0];
        if (!file) {
            setDraggingIndices(prev => {
                const next = new Set(prev);
                next.delete(index);
                return next;
            });
            return;
        }

        // Clear any previous errors
        setError(index, null);

        // Validate file type
        if (!file.type.match(/^image\/(jpeg|jpg|png|gif)$/)) {
            setError(index, 'Only JPG, PNG, and GIF images are allowed.');
            setDraggingIndices(prev => {
                const next = new Set(prev);
                next.delete(index);
                return next;
            });
            return;
        }

        // Validate file size (2MB)
        if (file.size > 2 * 1024 * 1024) {
            setError(index, 'File size must be less than 2MB.');
            setDraggingIndices(prev => {
                const next = new Set(prev);
                next.delete(index);
                return next;
            });
            return;
        }

        updateFile(file, index);
        setDraggingIndices(prev => {
            const next = new Set(prev);
            next.delete(index);
            return next;
        });
    }, [updateFile, setError]);

    const fileOnChange = React.useCallback((index) => (data) => {
        const file = data.target.files[0];
        if (!file) return;

        // Clear any previous errors
        setError(index, null);

        // Validate file type
        if (!file.type.match(/^image\/(jpeg|jpg|png|gif)$/)) {
            setError(index, 'Only JPG, PNG, and GIF images are allowed.');
            return;
        }

        // Validate file size (2MB)
        if (file.size > 2 * 1024 * 1024) {
            setError(index, 'File size must be less than 2MB.');
            return;
        }

        updateFile(file, index);
    }, [updateFile, setError]);

    const updateFile = React.useCallback(async (file, index) => {
        // File is already validated and compressed in fileOnChange/dropping
        setUploadingIndices(prev => new Set(prev).add(index));
        setError(index, null); // Clear error when starting upload
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('aim', 'vibes');
            formData.append('target', currentUserId || '');
            const response = await upload('/api/upload', formData);
            const json = await response.json();
            // Update the specific field using update method from useFieldArray
            update(index, {uri: json.uri});
            setError(index, null); // Clear error on success
        } catch (error) {
            console.error('Error uploading image:', error);
            setError(index, 'Failed to upload image. Please try again.');
        } finally {
            setUploadingIndices(prev => {
                const next = new Set(prev);
                next.delete(index);
                return next;
            });
        }
    }, [currentUserId, update, setError]);

    const removeImage = React.useCallback((index) => {
        remove(index);
    }, [remove]);

    const addMore = React.useCallback(() => {
        if (fields.length < maxImages) {
            append({uri: ''});
        }
    }, [append, fields.length, maxImages]);

    // Initialize with one empty field if no fields exist
    React.useEffect(() => {
        if (fields.length === 0) {
            append({uri: ''});
        }
    }, [fields.length, append]);

    return (
        <div className='media-form margin-top-1rem padding-1dot25rem border-radius-0dot75rem border-style-solid border-width-0dot0625rem border-color-gray-200'>
            <div className='display-flex align-items-center justify-content-space-between padding-bottom-1rem'>
                <div className='text-md semibold color-gray-900'>
                    <FormattedMessage defaultMessage='Media' />
                </div>
                <button
                    type='button'
                    onClick={onRemove}
                    className='text-sm color-error-600 hover-color-error-700 cursor-pointer background-color-transparent border-style-none padding-0'
                >
                    <FormattedMessage defaultMessage='Remove media' />
                </button>
            </div>
            <div className='display-flex flex-direction-column row-gap-0dot75rem'>
                {fields.map((field, index) => {
                    const isUploading = uploadingIndices.has(index);
                    const isDragging = draggingIndices.has(index);
                    const imageUri = field.uri || '';
                    const errorMessage = errors.get(index);

                    return (
                        <div key={field.id} className='display-flex flex-direction-column row-gap-0dot25rem'>
                            <div className='display-flex align-items-center column-gap-0dot5rem'>
                                <div className='flex-1'>
                                    <div className='background-color-gray-50 border-radius-0dot75rem padding-top-1dot25rem mw768-padding-top-1dot5rem padding-bottom-1dot25rem mw768-padding-bottom-1dot5rem padding-left-1rem mw768-padding-left-1dot5rem padding-right-1rem mw768-padding-right-1dot5rem'>
                                        {!imageUri ? (
                                            <label
                                                onDrop={dropping(index)}
                                                onDragLeave={endDragging(index)}
                                                onDragOver={startDragging(index)}
                                                htmlFor={`media-file-${index}`}
                                                className={classNames('display-block input-file-zone padding-top-1rem padding-bottom-1rem padding-left-1dot5rem padding-right-1dot5rem border-radius-0dot5rem border-1px-solid border-color-gray-200 background-color-white cursor-pointer position-relative', {
                                                    'input-file-zone--active': isDragging || isUploading
                                                })}
                                            >
                                                <div className={classNames('pointer-events-none', {'opacity-0 visibility-hidden': isUploading})}>
                                                    <input
                                                        id={`media-file-${index}`}
                                                        type='file'
                                                        onChange={fileOnChange(index)}
                                                        accept='.jpg,.png,.gif,.jpeg'
                                                        className='display-none'
                                                    />
                                                    <div className='display-flex justify-content-center pointer-events-none'>
                                                        <IconFeature {...{color: 'gray', feature: 'outline', size: 'md'}}>
                                                            <UploadCloud02Icon/>
                                                        </IconFeature>
                                                    </div>
                                                    <div className='padding-top-0dot75rem pointer-events-none'>
                                                        <p className='text-sm text-align-center'>
                                                            <span className='input-file-zone__text-accent color-primary-600'>
                                                                <FormattedMessage defaultMessage='Click to download'/>
                                                            </span>
                                                            {' '}
                                                            <span className='input-file-zone__text-regular color-gray-500'>
                                                                <FormattedMessage defaultMessage='or drag the image here'/>
                                                            </span>
                                                        </p>
                                                        <p className='input-file-zone__text-regular padding-top-0dot25rem text-xs color-gray-500 text-align-center'>
                                                            <span>PNG, JPG <FormattedMessage defaultMessage='or'/> GIF <FormattedMessage defaultMessage='up to'/></span>
                                                            <span className='semibold'> 2MB</span>
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className={classNames({'display-none pointer-events-none': !isUploading, 'input-file-zone__loader': isUploading})}>
                                                    <LogoLoading className='display-block width-3rem height-3rem'/>
                                                </div>
                                            </label>
                                        ) : (
                                            <React.Suspense fallback={<Skeleton/>}>
                                                <div className='position-relative vibe-post-media'>
                                                    <div className='vibe-post-media__img-container'>
                                                        <ErrorBoundary {...{FallbackComponent: ErrorFallback}}>
                                                            <SuspenseImage
                                                                className='vibe-post-media__img width-100percent height-100percent display-block'
                                                                src={`https://${imageUri}`}
                                                            />
                                                        </ErrorBoundary>
                                                    </div>
                                                    <div className='vibe-post-media__actions position-absolute top-50percent left-50percent transform-translate-center'>
                                                        <label
                                                            htmlFor={`media-file-replace-${index}`}
                                                            className='vibe-post-media__action color-white'
                                                            title='Replace image'
                                                        >
                                                            <input
                                                                id={`media-file-replace-${index}`}
                                                                type='file'
                                                                onChange={fileOnChange(index)}
                                                                accept='.jpg,.png,.gif,.jpeg'
                                                                className='display-none'
                                                            />
                                                            <UploadCloud02Icon className='display-block height-2rem width-2rem'/>
                                                        </label>
                                                        <div
                                                            className='vibe-post-media__action color-white'
                                                            onClick={() => {
                                                                update(index, {uri: ''});
                                                            }}
                                                        >
                                                            <Trash03Icon className='display-block height-2rem width-2rem'/>
                                                        </div>
                                                    </div>
                                                </div>
                                            </React.Suspense>
                                        )}
                                    </div>
                                </div>
                                {fields.length > 1 && (
                                    <Button
                                        type='button'
                                        size='sm'
                                        color='error'
                                        plain={true}
                                        clickHandler={() => {
                                            setError(index, null); // Clear error when removing
                                            removeImage(index);
                                        }}
                                        additionalClasses='btn---icon-plain-square flex-shrink-0'
                                    >
                                        <XCloseIcon className='display-block width-1dot25rem height-1dot25rem'/>
                                    </Button>
                                )}
                            </div>
                            {errorMessage && (
                                <p className='text-sm color-error-600 padding-top-0dot25rem'>
                                    {errorMessage}
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>
            {fields.length < maxImages && (
                <div className='padding-top-1rem'>
                    <Button
                        type='button'
                        size='md'
                        color='primary'
                        plain={true}
                        clickHandler={addMore}
                    >
                        <FormattedMessage defaultMessage='Add more' />
                    </Button>
                </div>
            )}
        </div>
    );
});

export default MediaForm;
